import React, { useEffect, useState, useMemo } from "react";
import QuillEditor from "@/modules/Email/components/QuillEditor";
import type {
  EmailTemplateDetail,
  EmailTemplateUpdatePayload,
} from "@/service/emailTemplate.service";

/**
 * The API body is a full HTML document (<!DOCTYPE html>...).
 * Quill can only edit content fragments, so we extract the <body> inner
 * content for editing and re-wrap it on save.
 */
const extractBodyContent = (fullHtml: string): { wrapper: { before: string; after: string }; content: string } => {
  const bodyOpenMatch = fullHtml.match(/<body[^>]*>/i);
  const bodyCloseMatch = fullHtml.match(/<\/body>/i);

  if (bodyOpenMatch && bodyCloseMatch) {
    const bodyOpenEnd = fullHtml.indexOf(bodyOpenMatch[0]) + bodyOpenMatch[0].length;
    const bodyCloseStart = fullHtml.indexOf(bodyCloseMatch[0]);
    return {
      wrapper: {
        before: fullHtml.substring(0, bodyOpenEnd),
        after: fullHtml.substring(bodyCloseStart),
      },
      content: fullHtml.substring(bodyOpenEnd, bodyCloseStart),
    };
  }

  return { wrapper: { before: "", after: "" }, content: fullHtml };
};

const rebuildFullHtml = (
  wrapper: { before: string; after: string },
  content: string
): string => {
  if (!wrapper.before && !wrapper.after) return content;
  return wrapper.before + content + wrapper.after;
};

interface TemplateEditModalProps {
  template: EmailTemplateDetail;
  viewMode?: boolean;
  saving?: boolean;
  onCancel: () => void;
  onSave: (payload: EmailTemplateUpdatePayload) => void;
}

const TemplateEditModal: React.FC<TemplateEditModalProps> = ({
  template,
  viewMode = false,
  saving = false,
  onCancel,
  onSave,
}) => {
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [wrapper, setWrapper] = useState<{ before: string; after: string }>({
    before: "",
    after: "",
  });
  const [originalContent, setOriginalContent] = useState("");

  useEffect(() => {
    setSubject(template.subject);
    const extracted = extractBodyContent(template.body);
    setWrapper(extracted.wrapper);
    setContent(extracted.content);
    setOriginalContent(extracted.content);
  }, [template]);

  const canSave = useMemo(() => {
    if (viewMode || saving) return false;
    const subjectChanged = subject.trim() !== template.subject.trim();
    const contentChanged = content.trim() !== originalContent.trim();
    return !!subject.trim() && !!content.trim() && (subjectChanged || contentChanged);
  }, [subject, content, template, originalContent, viewMode, saving]);

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      code: template.code,
      name: template.name,
      subject: subject.trim(),
      body: rebuildFullHtml(wrapper, content),
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-[18px] font-[600] text-[#1E1E1E] mb-2">
          Email content
        </h2>
        <div className="flex flex-col gap-2">
          <label className="text-sm text-[#1E1E1E] font-[500]">
            Subject
            <span className="text-[#DC2626] ml-0.5">*</span>
          </label>
          <input
            type="text"
            className="h-[42px] px-3 border border-gray-200 rounded-[6px] focus:outline-none text-sm"
            placeholder="Enter subject"
            value={subject}
            readOnly={viewMode}
            onChange={(e) => setSubject(e.target.value)}
          />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className="text-sm text-[#1E1E1E] font-[500]">
          Email content
          <span className="text-[#DC2626] ml-0.5">*</span>
        </label>
        <div className="border border-gray-200 rounded-[6px] overflow-hidden">
          <QuillEditor
            value={content}
            onChange={setContent}
            placeholder="Type your email..."
            readOnly={viewMode}
          />
        </div>
      </div>

      <div className="flex items-center justify-end gap-4 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="h-[36px] px-4 rounded-md bg-[#F5F5F5] text-[#1E1E1E] text-sm hover:bg-gray-100 cursor-pointer"
        >
          Cancel
        </button>
        {!viewMode && (
          <button
            type="button"
            disabled={!canSave}
            onClick={handleSave}
            className="h-[36px] px-4 rounded-md text-white text-sm bg-[#73B5C5] hover:bg-[#5da7b8] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {saving ? "Updating..." : "Update"}
          </button>
        )}
      </div>
    </div>
  );
};

export default TemplateEditModal;
